import axios from 'axios'
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import { useAuthStore } from '@/stores/auth'

type RequestConfig<D = any> = AxiosRequestConfig<D>

interface ApiHttpInstance extends AxiosInstance {
  get<T = any, D = any>(url: string, config?: RequestConfig<D>): Promise<T>
  delete<T = any, D = any>(url: string, config?: RequestConfig<D>): Promise<T>
  post<T = any, D = any>(url: string, data?: D, config?: RequestConfig<D>): Promise<T>
  put<T = any, D = any>(url: string, data?: D, config?: RequestConfig<D>): Promise<T>
  patch<T = any, D = any>(url: string, data?: D, config?: RequestConfig<D>): Promise<T>
  request<T = any, D = any>(config: RequestConfig<D>): Promise<T>
}

const resolveBaseUrl = () => {
  const envValue = import.meta.env?.VITE_API_BASE_URL?.trim()
  if (envValue) {
    return envValue.replace(/\/$/, '')
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin
  }
  return ''
}

const API_BASE_URL = resolveBaseUrl()

// 创建axios实例
const http = axios.create({
  baseURL: API_BASE_URL || undefined,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
}) as ApiHttpInstance

// 请求拦截器 - 添加认证token
http.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const authStore = useAuthStore()
    if (authStore.token) {
      const headers = (config.headers || {}) as Record<string, unknown>
      headers.Authorization = `Bearer ${authStore.token}`
      config.headers = headers as InternalAxiosRequestConfig['headers']
    }
    return config
  },
  (error) => {
    console.error('Request interceptor error:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器 - 统一处理响应和错误
http.interceptors.response.use(
  (response: AxiosResponse) => {
    const { data } = response

    // 统一的响应格式处理
    if (data.success === false) {
      console.error('API Error:', data.message)
      const bizError: any = new Error(data.message || '请求失败')
      bizError.responseData = data
      throw bizError
    }

    return data
  },
  async (error) => {
    console.error('Response interceptor error:', error)

    // 处理401未授权错误
    if (error.response?.status === 401) {
      const authStore = useAuthStore()
      authStore.logout()
      return Promise.reject(new Error('用户未认证，请重新登录'))
    }

    if (
      error.response?.status === 403 &&
      /^\/api\/merchant\/(members|children|shops|points|employees|rules\/(points|discount))(\/|$)/.test(
        error.config?.url || ''
      )
    ) {
      try {
        await useAuthStore().refreshProfile()
      } catch {
        // Preserve the original denial when permissions cannot be refreshed.
      }
    }

    // 处理其他HTTP错误
    const serverData = error.response?.data
    const errorMessage = serverData?.message || error.message || '网络请求失败'
    const wrappedError: any = new Error(errorMessage)
    wrappedError.responseData = serverData
    return Promise.reject(wrappedError)
  }
)

export default http
